import {
    createContext,
    Dispatch,
    useCallback,
    useContext,
    useMemo,
    useState,
} from 'react'
import {
    DiscountContractWithItemId,
    ISelectItemInCart,
    MenuViewModel,
    OrderItemViewModel,
    SelectItemInCartType,
} from 'store/models/sellModels'
import useTableSaleSelectReducer, {
    ITSPOSState,
    TablePOSAction,
} from '../hooks/useSaleSelectReducer/useSaleSelectReducer'
import {
    OrderDetailContract,
    OrderLineKdsStatus,
    OrderLineStatus,
    OrderOwnerContract,
    OrderPaymentStatus,
    OrderStatus,
} from 'models'
import { useSaleDependenciesContext } from './SaleDependenciesContext'
import OrderItemViewError from 'store/utils/OrderItemViewError'
import { usePosContext } from './PosProvider'
import { useSelectDefaultMenu } from '../TableService/hooks/useSelectDefaultMenu'
import {
    changeBaseItemIDToVariantID,
    isSameOrderItem,
    isUseSamePriceList,
} from 'components/Sell/utils/common'
import { isEqual } from 'lodash-es'
import { updateLineTotalWithDiscount } from '../utils/orderItemUtil'
import {
    autoDiscountByItemsMapping,
    getItemLevelAutomaticDiscounts,
} from '../discount/discountMapper/discountMapper'
import { validateAvailability } from 'utilities/availability/availabilityHelper'

export interface ITableSaleSelectContext extends ITSPOSState {
    dispatch: Dispatch<TablePOSAction>
    unableAddItem: boolean
    relatedOrders: OrderOwnerContract[]
}

const TableSaleSelectContext = createContext<ITableSaleSelectContext>(
    {} as ITableSaleSelectContext,
)

export function TableSaleSelectContextProvider({
    children,
}: {
    children: React.ReactNode
}) {
    const [saleSelect, dispatch] = useTableSaleSelectReducer()
    const { relatedOrders, focusOrder } = saleSelect

    const unableAddItem = useMemo(
        () =>
            (relatedOrders?.every((i) => i.isPaid || i.isPartiallyPaid) ??
                false) ||
            !focusOrder,
        [relatedOrders, focusOrder],
    )

    useSelectDefaultMenu(dispatch)

    return (
        <TableSaleSelectContext.Provider
            value={{
                ...saleSelect,
                dispatch,
                unableAddItem,
                relatedOrders,
            }}
        >
            {children}
        </TableSaleSelectContext.Provider>
    )
}

export const useTableSaleSelectContext = () => {
    const props = useContext(TableSaleSelectContext)
    const {
        strictTimeAvailabilityOnSales,
        disableProductSelectionForOutOfStockItems: enableInventoryCheck,
    } = usePosContext()
    const {
        dispatch,
        relatedOrders,
        selectedItemInCart,
        cartOrderItems,
        fulfillmentType,
        excludeAutoDiscountIds,
        orderChannel,
        selectedMenuSectionId,
        selectedMenu,
        preventEditLines,
    } = props
    const {
        menus,
        onlineStores,
        discounts,
        comboChoicesMap,
        unavailableProducts,
        modifierOptionsMap,
        priceListData,
        variantGroupsMap,
        modifierGroupsMap,
        itemsMap,
        entityModifiersMap,
        addItemCounting,
    } = useSaleDependenciesContext()

    const [orderNote, setOrderNote] = useState<string>('')

    const defaultOrder = useMemo(() => {
        return relatedOrders?.find((a) => !a.isPaid && !a.isVoid)
    }, [relatedOrders])

    const isSelectedMenuSectionUnavailable = useMemo(
        () =>
            !validateAvailability(
                selectedMenu?.menuSectionAvailabilitiesMap?.[
                    selectedMenuSectionId
                ],
            ),
        [selectedMenu, selectedMenuSectionId],
    )
    const setLoadAllOrders = useCallback(
        (load: boolean) => {
            dispatch({ type: 'TS_SET_RELOAD_ALL_ORDER', payload: load })
        },
        [dispatch],
    )
    const setSelectCartItem = useCallback(
        (data: ISelectItemInCart) => {
            dispatch({
                type: 'SELECT_CART_ITEM',
                payload: data,
            })
        },
        [dispatch],
    )
    const onSelectOrderCustomer = useCallback(
        (orderCustomerID: string, customerAddressID?: string) => {
            dispatch({
                type: 'ASSIGN_CUSTOMER_TO_ORDER',
                payload: {
                    customerID: orderCustomerID,
                    customerAddressID: customerAddressID,
                },
            })
        },
        [dispatch],
    )
    /** change focus order (Mostly use when change focus seat / picked new table / group) */
    const handleFocusOrder = useCallback(
        (order?: OrderOwnerContract) => {
            dispatch({
                type: 'SET_FOCUS_ORDER',
                payload: order,
            })
            const orderCustomerID = order?.customerID

            // Auto select customer to current focus order's customer
            onSelectOrderCustomer(orderCustomerID!)
        },
        [dispatch, onSelectOrderCustomer],
    )
    const setSelectedItem = useCallback(
        (item: OrderItemViewModel) => {
            dispatch({
                type: 'SELECT_CART_ITEM',
                payload: {
                    data: item,
                    type: SelectItemInCartType.item,
                },
            })
        },
        [dispatch],
    )

    const mergeDiscountItem = useCallback(
        (discountLineItems: OrderItemViewModel[]) => {
            dispatch({
                type: 'MERGE_DISCOUNT_ITEM',
                payload: discountLineItems,
            })
        },
        [dispatch],
    )

    const openCustomerDialog = useCallback(() => {
        dispatch({
            type: 'SET_CUSTOMER_SELECTION_DIALOG_STATUS',
            payload: true,
        })
    }, [dispatch])

    const closeCustomerDialog = useCallback(() => {
        dispatch({
            type: 'SET_CUSTOMER_SELECTION_DIALOG_STATUS',
            payload: false,
        })
    }, [dispatch])

    const openCartDiscountDialog = useCallback(() => {
        dispatch({
            type: 'SET_CART_DISCOUNT_DIALOG_STATUS',
            payload: true,
        })
    }, [dispatch])

    const closeCartDiscountDialog = useCallback(() => {
        dispatch({
            type: 'SET_CART_DISCOUNT_DIALOG_STATUS',
            payload: false,
        })
    }, [dispatch])

    const startEditSelectedItem = useCallback(() => {
        if (!selectedItemInCart) return

        if (selectedItemInCart.type === SelectItemInCartType.item) {
            dispatch({
                type: 'SET_IS_EDITING_SELECTED_ITEM',
                payload: true,
            })
        }
    }, [dispatch, selectedItemInCart])

    const endEditSelectedItem = useCallback(() => {
        dispatch({
            type: 'SET_IS_EDITING_SELECTED_ITEM',
            payload: false,
        })
    }, [dispatch])

    const openServiceChargeDialog = useCallback(
        (selectedTableIds: string[]) => {
            dispatch({
                type: 'SET_SERVICE_CHARGE_DIALOG_STATUS',
                payload: {
                    status: true,
                    selectedTableIds: selectedTableIds,
                },
            })
        },
        [dispatch],
    )

    const closeServiceChargeDialog = useCallback(() => {
        dispatch({
            type: 'SET_SERVICE_CHARGE_DIALOG_STATUS',
            payload: {
                status: false,
                selectedTableIds: [],
            },
        })
    }, [dispatch])

    const openSendToKitchenDialog = useCallback(() => {
        dispatch({ type: 'SET_SEND_TO_KITCHEN_DIALOG_STATUS', payload: true })
    }, [dispatch])

    const closeSendToKitchenDialog = useCallback(() => {
        dispatch({ type: 'SET_SEND_TO_KITCHEN_DIALOG_STATUS', payload: false })
    }, [dispatch])

    const clearItemCart = useCallback(() => {
        dispatch({
            type: 'CLEAR_CART_ITEMS',
        })
    }, [dispatch])

    const removeCartItems = useCallback(
        (orderItemIds: string[]) => {
            dispatch({
                type: 'REMOVE_CART_ITEMS',
                payload: orderItemIds,
            })
        },
        [dispatch],
    )

    const setRelatedOrders = useCallback(
        (orders: OrderOwnerContract[]) => {
            dispatch({
                type: 'SET_RELATED_ORDERS',
                payload: orders,
            })
        },
        [dispatch],
    )
    const updateRelatedOrder = useCallback(
        (order: OrderDetailContract) => {
            const orders = relatedOrders.map<OrderOwnerContract>((o) => {
                if (o.orderID === order.orderID) {
                    return {
                        ...o,
                        isPaid:
                            order.orderPaymentStatus ===
                            OrderPaymentStatus.Paid,
                        isPartiallyPaid:
                            order.orderPaymentStatus ===
                            OrderPaymentStatus.PartiallyPaid,
                        OrderID: order.orderID,

                        OrderNo: order.orderNo,
                        CustomerID: order.customerID,
                        IsVoid: order.status === OrderStatus.Voided,
                        IsSent:
                            order.orderLines?.every(
                                (i) =>
                                    i.kdsStatus !== OrderLineKdsStatus.Unknown,
                            ) ?? false,
                        AliasName: order.aliasName,
                        HasOrderLines: order.orderLines?.some(
                            (e) => !e.isSharedLine,
                        ),
                        HasNonVoidOrderLines: order.orderLines?.some(
                            (e) => e.status !== OrderLineStatus.Voided,
                        ),
                        HasSharedLines: order.orderLines?.some(
                            (e) => e.isSharedLine,
                        ),
                        OrderDateTime: order.orderDateTime,
                    }
                } else return { ...o }
            })
            dispatch({
                type: 'SET_RELATED_ORDERS',
                payload: orders,
            })
        },
        [dispatch, relatedOrders],
    )
    const setCartItems = useCallback(
        (items: OrderItemViewModel[]) => {
            dispatch({
                type: 'SET_CART_ITEMS',
                payload: items,
            })
        },
        [dispatch],
    )
    const onSelectOnlineStore = useCallback(
        (onlineStoreId: string) => {
            const store = onlineStores.find(
                (a) => a.onlineStoreID === onlineStoreId,
            )!
            dispatch({
                type: 'SELECT_ONLINE_STORE',
                payload: { selectOnlineStore: store, menus },
            })
        },
        [dispatch, menus, onlineStores],
    )
    const onSelectBrand = useCallback(
        (brandID: string) => {
            dispatch({ type: 'SELECT_BRNAD', payload: brandID })
        },
        [dispatch],
    )
    const onSelectMenuSection = useCallback(
        (id: string) => {
            dispatch({ type: 'SELECT_MENUSECTION', payload: id })
        },
        [dispatch],
    )
    const onSelectCategory = useCallback(
        (categoryId: string) => {
            dispatch({
                type: 'SELECT_CATEGORY',
                payload: categoryId,
            })
        },
        [dispatch],
    )
    const onSelectMenu = useCallback(
        (menu: MenuViewModel) => {
            dispatch({
                type: 'SELECT_MENU',
                payload: {
                    selectedMenu: menu,
                },
            })
        },
        [dispatch],
    )
    const removeCartItem = useCallback(
        (orderItemView: OrderItemViewModel) => {
            dispatch({
                type: 'REMOVE_SALE_ITEM',
                payload: {
                    type: SelectItemInCartType.item,
                    data: orderItemView,
                } as ISelectItemInCart,
            })
        },
        [dispatch],
    )

    const addSaleItems = useCallback(
        (newOrderItems: OrderItemViewModel[]) => {
            const errors = new OrderItemViewError(
                strictTimeAvailabilityOnSales,
                menus,
                comboChoicesMap,
                unavailableProducts,
                enableInventoryCheck ?? false,
                modifierOptionsMap,
                priceListData,
                variantGroupsMap,
                modifierGroupsMap,
                itemsMap,
                entityModifiersMap,
            )
            newOrderItems.forEach((l) => {
                errors.validateOrderItemsView(l)
                l.error = errors.errors
                l = changeBaseItemIDToVariantID(l)
            })

            let orderItemsAppended: OrderItemViewModel[] = cartOrderItems
            let latestOrderItem: OrderItemViewModel = newOrderItems[0]

            newOrderItems.forEach((l) => {
                const { newOrderItems, newAddedItem } = addItemCounting(
                    orderItemsAppended,
                    l,
                    preventEditLines.map((a) => a.orderLineID),
                )
                orderItemsAppended = [...newOrderItems]
                latestOrderItem = newAddedItem
            })
            // Apply Automatic discount
            const itemLevelAutoDiscounts = getItemLevelAutomaticDiscounts(
                discounts,
            )

            const newCartOrderItemsWithDiscounts = autoDiscountByItemsMapping({
                automaticDiscounts: itemLevelAutoDiscounts,
                checkingFulfillmentType: fulfillmentType,
                excludeAutoDiscountIds: excludeAutoDiscountIds,
                orderChannel: orderChannel,
            })(orderItemsAppended)

            newCartOrderItemsWithDiscounts.forEach((i) =>
                updateLineTotalWithDiscount(i),
            )

            dispatch({
                type: 'ADD_SALE_ITEM',
                payload: {
                    newCartOrderItems: newCartOrderItemsWithDiscounts,
                    latestOrderItem: newCartOrderItemsWithDiscounts.find(
                        (i) => i.id === latestOrderItem.id,
                    ),
                },
            })
        },
        [
            addItemCounting,
            cartOrderItems,
            comboChoicesMap,
            discounts,
            dispatch,
            enableInventoryCheck,
            entityModifiersMap,
            excludeAutoDiscountIds,
            fulfillmentType,
            itemsMap,
            menus,
            modifierGroupsMap,
            modifierOptionsMap,
            orderChannel,
            preventEditLines,
            priceListData,
            strictTimeAvailabilityOnSales,
            unavailableProducts,
            variantGroupsMap,
        ],
    )

    const addSaleItem = useCallback(
        (newOrderItem: OrderItemViewModel) => {
            addSaleItems([newOrderItem])
        },
        [addSaleItems],
    )
    const onSelectDiscountOrComp = useCallback(
        (itemCart: ISelectItemInCart) => {
            dispatch({
                type: 'SELECT_CART_ITEM',
                payload: itemCart,
            })
        },
        [dispatch],
    )
    const onUpdateItem = useCallback(
        (orderItemId: string, newOrderItem: OrderItemViewModel) => {
            newOrderItem.isSaved = false
            const editingItem = cartOrderItems.find((o) => o.id === orderItemId)
            if (editingItem === undefined) return

            const newItem = changeBaseItemIDToVariantID(newOrderItem)

            let newOrderItems = [...cartOrderItems]

            const existOrderItem = cartOrderItems
                .filter((i) => i.id !== newItem.id)
                .find(
                    (i) =>
                        isSameOrderItem(i, newItem) &&
                        isUseSamePriceList(menus, priceListData, i, newItem),
                )

            if (existOrderItem) {
                existOrderItem.amount += newItem.amount
                existOrderItem.isSaved = false
                updateLineTotalWithDiscount(existOrderItem)

                // removed old edited item after merge with existing item
                newOrderItems = newOrderItems.filter(
                    (i) => !isEqual(i, editingItem),
                )
            } else {
                // replace with new item
                newOrderItems = newOrderItems.map((e) => {
                    if (isEqual(e, editingItem)) {
                        updateLineTotalWithDiscount(newItem)
                        return newItem
                    }
                    return e
                })
            }

            dispatch({
                type: 'UPDATE_SALE_ITEM',
                payload: {
                    newOrderItems,
                    latestOrderItem: newItem,
                },
            })
        },
        [dispatch, cartOrderItems, menus, priceListData],
    )
    const onSearchSaleItem = useCallback(
        (searchString: string) => {
            dispatch({
                type: 'SEARCH_SALE_ITEM',
                payload: searchString,
            })
        },
        [dispatch],
    )

    const removeDiscountItem = useCallback(
        (discount: DiscountContractWithItemId) => {
            dispatch({
                type: 'REMOVE_ORDER_DISCOUNT_ITEM',
                payload: {
                    data: discount,
                },
            })
        },
        [dispatch],
    )
    const validateTableCartItemsView = useCallback(() => {
        dispatch({
            type: 'CHECK_ITEM_VIEW_ERROR',
            payload: {
                errorCheck: new OrderItemViewError(
                    strictTimeAvailabilityOnSales,
                    menus,
                    comboChoicesMap,
                    unavailableProducts,
                    enableInventoryCheck ?? false,
                    modifierOptionsMap,
                    priceListData,
                    variantGroupsMap,
                    modifierGroupsMap,
                    itemsMap,
                    entityModifiersMap,
                ),
            },
        })
    }, [
        dispatch,
        strictTimeAvailabilityOnSales,
        menus,
        comboChoicesMap,
        unavailableProducts,
        enableInventoryCheck,
        modifierOptionsMap,
        priceListData,
        variantGroupsMap,
        modifierGroupsMap,
        itemsMap,
        entityModifiersMap,
    ])

    const setNoteToOrder = useCallback(
        (orderId: string, note: string) => {
            dispatch({
                type: 'SET_NOTE_TO_ORDER',
                payload: {
                    orderId,
                    note: note,
                },
            })
        },
        [dispatch],
    )
    const addPreventEditLines = useCallback(
        (lineIDs: string[]) => {
            dispatch({ type: 'ADD_PREVENT_EDIT_LINES', payload: lineIDs })
        },
        [dispatch],
    )
    const removePreventEditLines = useCallback(
        (lineIDs: string[]) => {
            dispatch({ type: 'REMOVE_PREVENT_EDIT_LINES', payload: lineIDs })
        },
        [dispatch],
    )
    const clearPreventEditLines = useCallback(() => {
        dispatch({
            type: 'CLEAR_PREVENT_EDIT_LINES',
        })
    }, [dispatch])
    return {
        ...props,
        setSelectedItem,
        setSelectCartItem,
        mergeDiscountItem,
        openCustomerDialog,
        closeCustomerDialog,
        openCartDiscountDialog,
        closeCartDiscountDialog,
        startEditSelectedItem,
        endEditSelectedItem,
        openServiceChargeDialog,
        closeServiceChargeDialog,
        orderNote,
        addNote: setOrderNote,
        clearItemCart,
        removeCartItems,
        removeCartItem,
        openSendToKitchenDialog,
        closeSendToKitchenDialog,
        handleFocusOrder,
        onSelectOrderCustomer,
        defaultOrder,
        setRelatedOrders,
        setCartItems,
        onSelectOnlineStore,
        onSelectBrand,
        onSelectMenu,
        onSelectCategory,
        addSaleItem,
        addSaleItems,
        onSelectDiscountOrComp,
        onUpdateItem,
        removeDiscountItem,
        onSearchSaleItem,
        onSelectMenuSection,
        validateTableCartItemsView,
        updateRelatedOrder,
        setNoteToOrder,
        isSelectedMenuSectionUnavailable,
        addPreventEditLines,
        removePreventEditLines,
        clearPreventEditLines,
        setLoadAllOrders,
    }
}
export default TableSaleSelectContext
